import axios from "axios"
import Swal from "sweetalert2"
import secureLocalStorage from "react-secure-storage"
import configure from "configure"
import { axiosInstance, axiosNodeInstance } from "./config"

// Login endpoints: sent without a token, and may legitimately return 401 (wrong credentials)
const LOGIN_URL = /\/(auth\/)?login\/?(\?|$)/

// Only our own backends get the token, never third-party URLs
const API_ORIGINS = [configure.apiUrl, configure.nodeApi]
  .filter(Boolean)
  .map(url => new URL(url).origin)

let isHandling = false

export const getAuthToken = () => secureLocalStorage.getItem("auth_token")

const isLoginUrl = url => LOGIN_URL.test(`${url || ""}`)

const isOwnApi = (url, baseURL) => {
  try {
    return API_ORIGINS.includes(new URL(url || "", baseURL || window.location.href).origin)
  } catch {
    return false
  }
}

// Missing, or set from an empty store ("Bearer null" / "Bearer undefined")
const hasValidAuthHeader = value => !!value && !/^Bearer\s*(null|undefined)?\s*$/i.test(`${value}`)

const isOnLoginPage = () => window.location.pathname.startsWith(`${configure.appUrl}login`)

const clearAuth = () => {
  localStorage.removeItem("auth_id")
  localStorage.removeItem("auth_name")
  localStorage.removeItem("auth_avt")
  localStorage.removeItem("auth_role")
  localStorage.removeItem("auth_email")
  secureLocalStorage.removeItem("auth_token")
  secureLocalStorage.removeItem("isWfh")
}

export const handleUnauthorized = () => {
  // Only one prompt even if many requests fail at once
  if (isHandling || isOnLoginPage() || !localStorage.getItem("auth_id")) return
  isHandling = true

  Swal.fire({
    title: "Session Expired",
    text: "Your session has expired. Please log in again.",
    icon: "warning",
    confirmButtonText: "Login",
    allowOutsideClick: false,
    allowEscapeKey: false,
  }).then(() => {
    clearAuth()
    window.location.href = `${configure.appUrl}login`
  })
}

const attachInterceptors = instance => {
  instance.interceptors.request.use(config => {
    const token = getAuthToken()
    if (token && !isLoginUrl(config.url) && isOwnApi(config.url, config.baseURL)) {
      const current = config.headers?.get ? config.headers.get("Authorization") : config.headers?.Authorization
      if (!hasValidAuthHeader(current)) {
        if (config.headers?.set) config.headers.set("Authorization", `Bearer ${token}`)
        else config.headers = { ...config.headers, Authorization: `Bearer ${token}` }
      }
    }
    return config
  })

  instance.interceptors.response.use(
    response => response,
    error => {
      if (error?.response?.status === 401 && !isLoginUrl(error?.config?.url)) {
        handleUnauthorized()
      }
      return Promise.reject(error)
    }
  )
}

export const setupAuthInterceptors = () => {
  // axios.create() instances don't inherit the default instance's interceptors
  ;[axios, axiosInstance, axiosNodeInstance].forEach(attachInterceptors)

  const originalFetch = window.fetch
  window.fetch = async (input, init = {}) => {
    const url = input?.url || input
    const token = getAuthToken()
    if (token && !isLoginUrl(url) && isOwnApi(url)) {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined))
      if (!hasValidAuthHeader(headers.get("Authorization"))) {
        headers.set("Authorization", `Bearer ${token}`)
        init = { ...init, headers }
      }
    }
    const response = await originalFetch(input, init)
    if (response.status === 401 && !isLoginUrl(url)) {
      handleUnauthorized()
    }
    return response
  }

  // Existing code dispatches this event manually
  window.addEventListener("session_expired", handleUnauthorized)
}
